/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { Accordion, Button, Dimmer, Icon, Label, LabelGroup, Loader } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { FindingSuggestion, FindingSuggestionsForPriority, Priority } from '../../model/assessmentDetail.model';
import { ModalPage } from '../../../../shared/ModalPage';
import style from '../checkList.style';
import { useAppDispatch } from '../../../../core/store';
import {
  changeFindingSuggestions,
  fetchFindingSuggestionsForPriority,
  selectFindingSuggestionsFilters,
  selectIsFetching,
  selectShowResults,
  setShowResults,
} from '../../store/findingSuggestionsSlice';
import { Brand } from '../../../../shared/model/brand.model';
import { useCtrlEnterToRunCallback, useEscToRunCallback } from '../../../../core/utils';
import { FilterBadge } from '../../../../shared/FilterBadge';
import { emptyFindingSuggestionsFilters } from './findingSuggestionsQueryParams';
import { BrandMultiSelectField } from './BrandMultiSelectField';
import { RegionMultiSelectField } from './RegionMultiSelectField';
import { CountryMultiSelectField } from './CountryMultiSelectField';

interface PrioritySelectionHelperProps {
  processCode?: string;
  subProcessCode?: string;
  subProcessDescription?: string;
  setPastExamplesOpen?: (value: boolean) => void;
  findingSuggestions?: FindingSuggestionsForPriority[];
  onClickFindingSuggestion?: (priority: Priority, finding: string, recommendation: string) => void;
}

export const PrioritySelectionHelper = ({
  processCode,
  subProcessCode,
  subProcessDescription,
  setPastExamplesOpen = () => null,
  findingSuggestions = [],
  onClickFindingSuggestion = (priority: Priority, finding: string, recommendation: string) => null,
}: PrioritySelectionHelperProps): JSX.Element => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dispatch = useAppDispatch();
  const showResults = useSelector(selectShowResults);
  const isFetching = useSelector(selectIsFetching);

  const toggleAccordion = (i: number) => {
    if (activeIndex === i) {
      setActiveIndex(-1);
    } else {
      setActiveIndex(i);
    }
  };

  return (
    <ModalPage
      unsavedChangesPopupDisabled
      title={t('assessment.audit.fixListTitle') || ''}
      onClose={() => {
        setPastExamplesOpen(false);
        dispatch(setShowResults(true));
        dispatch(changeFindingSuggestions([]));
      }}>
      <div css={style.container} style={{ overflow: 'auto' }}>
        <div css={style.title}>{`${subProcessCode} ${subProcessDescription}`}</div>

        {!showResults && <FindingSuggestionsFilters processCode={processCode} subProcessCode={subProcessCode} />}
        {showResults && (
          <>
            <FiltersBadgesContainer />
            {isFetching && (
              <Dimmer active inverted>
                <Loader inverted content='Loading' />
              </Dimmer>
            )}
            {findingSuggestions.map((findingSuggestionsForPriority, index) => {
              return (
                <FindingSuggestionsForPriorityItem
                  index={index}
                  onClickFindingSuggestion={onClickFindingSuggestion}
                  findingSuggestionsForPriority={findingSuggestionsForPriority}
                  toggleAccordion={toggleAccordion}
                  activeIndex={activeIndex}
                />
              );
            })}
          </>
        )}
      </div>
    </ModalPage>
  );
};

interface FindingSuggestionsForPriorityItemProps {
  index: number;
  onClickFindingSuggestion: (priority: Priority, finding: string, recommendation: string) => void;
  findingSuggestionsForPriority: FindingSuggestionsForPriority;
  toggleAccordion: (index: number) => void;
  activeIndex: number;
}
const FindingSuggestionsForPriorityItem = ({
  index,
  onClickFindingSuggestion,
  findingSuggestionsForPriority,
  toggleAccordion,
  activeIndex,
}: FindingSuggestionsForPriorityItemProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Accordion css={style.priorityRow}>
      <Accordion.Title
        active={activeIndex === index}
        index={index}
        onClick={() => toggleAccordion(index)}
        css={style.priorityTitle}>
        <Icon name='dropdown' />
        <div>
          Priority: {t(`assessment.priority.${findingSuggestionsForPriority.priority}`)} (
          {findingSuggestionsForPriority.findingsCount})
        </div>
      </Accordion.Title>
      <Accordion.Content active={activeIndex === index}>
        <Accordion.Content active={activeIndex === index}>
          {findingSuggestionsForPriority.findingSuggestions.length > 0 ? (
            findingSuggestionsForPriority.findingSuggestions.map((findingSuggestion, findingSuggestionIndex) => {
              return (
                <FindingSuggestionItem
                  onClickFindingSuggestion={onClickFindingSuggestion}
                  findingSuggestionsForPriority={findingSuggestionsForPriority}
                  findingSuggestion={findingSuggestion}
                  findingSuggestionIndex={findingSuggestionIndex}
                />
              );
            })
          ) : (
            <div css={style.noSuggestionsLabel}>
              <i>{t('assessment.checkList.noSuggestions')}</i>
            </div>
          )}
        </Accordion.Content>
      </Accordion.Content>
    </Accordion>
  );
};

interface FindingSuggestionItemProps {
  onClickFindingSuggestion: (priority: Priority, finding: string, recommendation: string) => void;
  findingSuggestionsForPriority: FindingSuggestionsForPriority;
  findingSuggestion: FindingSuggestion;
  findingSuggestionIndex: number;
}
const FindingSuggestionItem = ({
  onClickFindingSuggestion,
  findingSuggestionsForPriority,
  findingSuggestion,
  findingSuggestionIndex,
}: FindingSuggestionItemProps): JSX.Element => {
  const dispatch = useAppDispatch();

  return (
    <div
      css={style.suggestionContainer}
      onClick={() => {
        onClickFindingSuggestion(
          findingSuggestionsForPriority.priority,
          findingSuggestion.finding,
          findingSuggestion.recommendation
        );
        dispatch(changeFindingSuggestions([]));
      }}>
      <div css={style.suggestionTitle} style={{ borderTopLeftRadius: '5px', borderTopRightRadius: '5px' }}>
        <div css={style.suggestionLabel}>Finding</div>
        <div css={style.suggestionIndex}>
          {findingSuggestionIndex + 1} of {findingSuggestionsForPriority.findingSuggestions.length}
        </div>
      </div>
      <div css={style.suggestionText}>{findingSuggestion.finding}</div>
      <div css={style.suggestionTitle} style={{ marginTop: '5px' }}>
        <div css={style.suggestionLabel}>Recommendation</div>
        <div css={style.suggestionIndex}>
          {findingSuggestionIndex + 1} of {findingSuggestionsForPriority.findingSuggestions.length}
        </div>
      </div>
      <div css={style.suggestionText} style={{ borderBottomLeftRadius: '5px', borderBottomRightRadius: '5px' }}>
        {findingSuggestion.recommendation}
      </div>
    </div>
  );
};

export const FiltersBadgesContainer = () => {
  const { t } = useTranslation();
  const findingSuggestionsFilters = useSelector(selectFindingSuggestionsFilters);
  const dispatch = useAppDispatch();

  const [numberOfFiltersApplied, setNumberOfFiltersApplied] = useState<number>(0);

  useEffect(() => {
    const n =
      (findingSuggestionsFilters.brands.length > 0 ? 1 : 0) +
      (findingSuggestionsFilters.regions.length > 0 ? 1 : 0) +
      (findingSuggestionsFilters.countries.length > 0 ? 1 : 0);
    setNumberOfFiltersApplied(n);
  }, [findingSuggestionsFilters]);

  const getFiltersArray = useCallback(() => {
    const filtersBadges = {
      brands: findingSuggestionsFilters.brands.map(b => b.description).join(', '),
      regions: findingSuggestionsFilters.regions.join(', '),
      countries: findingSuggestionsFilters.countries.join(', '),
    };

    return Object.keys(filtersBadges)
      .map(k => {
        const realValue = (findingSuggestionsFilters as any)[k];

        return {
          show: !_.isEqual(realValue, (emptyFindingSuggestionsFilters as any)[k]),
          field: k,
          description: (filtersBadges as any)[k],
          label: t(`assessment.report.filters.${k}`),
        };
      })
      .filter(f => f.show);
  }, [findingSuggestionsFilters, t]);

  const onFilterButtonClick = useCallback(() => {
    dispatch(setShowResults(false));
  }, [dispatch]);

  useEscToRunCallback(onFilterButtonClick);

  return (
    <div css={style.badgeGroup}>
      <LabelGroup circular>
        {getFiltersArray().map(f => (
          <div css={style.badges}>
            <FilterBadge value={f.description} label={f.label} />
          </div>
        ))}
      </LabelGroup>
      <div css={style.filterButtonContainer}>
        <Button css={style.filterButton} onClick={onFilterButtonClick}>
          <div css={style.icon}>
            <Icon name='filter' />
          </div>
          <span>{t('assessment.report.filtersText')}</span>
        </Button>
        {numberOfFiltersApplied > 0 && (
          <Label css={style.filterButtonBadge} floating circular>
            {numberOfFiltersApplied}
          </Label>
        )}
      </div>
    </div>
  );
};

interface FindingSuggestionsFiltersProps {
  processCode?: string;
  subProcessCode?: string;
}

const FindingSuggestionsFilters = ({ processCode, subProcessCode }: FindingSuggestionsFiltersProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const filters = useSelector(selectFindingSuggestionsFilters);

  const [brands, setBrands] = useState<Brand[]>(filters.brands);
  const [regions, setRegions] = useState<string[]>(filters.regions);
  const [countries, setCountries] = useState<string[]>(filters.countries);

  const getFilters = useCallback(
    () => ({
      brands,
      regions,
      countries,
    }),
    [brands, countries, regions]
  );

  const search = useCallback(() => {
    dispatch(fetchFindingSuggestionsForPriority(getFilters(), processCode, subProcessCode));
    dispatch(setShowResults(true));
  }, [dispatch, getFilters, processCode, subProcessCode]);

  const clear = useCallback(() => {
    setBrands([]);
    setRegions([]);
    setCountries([]);
  }, []);

  useCtrlEnterToRunCallback(search);

  return (
    <div css={style.filtersContainer}>
      <div>
        <BrandMultiSelectField
          filters={emptyFindingSuggestionsFilters}
          defaultValue={brands}
          editable
          onUpdate={selectedValues => {
            setBrands(selectedValues);
          }}
        />
        <RegionMultiSelectField
          filters={emptyFindingSuggestionsFilters}
          defaultValue={regions}
          editable
          onUpdate={selectedValues => {
            setRegions(selectedValues);
            setCountries(emptyFindingSuggestionsFilters.countries);
          }}
        />
        <CountryMultiSelectField
          filters={{
            ...emptyFindingSuggestionsFilters,
            regions: getFilters().regions,
          }}
          defaultValue={countries}
          editable={regions.length > 0}
          onUpdate={selectedValues => {
            setCountries(selectedValues);
          }}
        />
      </div>

      <div css={style.buttonsContainer}>
        <Button css={style.button} onClick={clear}>
          <Icon name='eraser' />
          {t('assessment.report.clear')}
        </Button>
        <Button css={style.button} onClick={search}>
          <Icon name='search' />
          {t('assessment.report.search')}
        </Button>
      </div>
    </div>
  );
};
